import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Award, Star, Plus, Loader2, Phone, ArrowLeft, Trash2, ChevronRight, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { recommendationAPI } from '../../services/api';

const CATEGORY_META = {
  plumber: { label: 'Plumber', emoji: '🔧' },
  electrician: { label: 'Electrician', emoji: '⚡' },
  carpenter: { label: 'Carpenter', emoji: '🪵' },
  painter: { label: 'Painter', emoji: '🎨' },
  cook: { label: 'Cook / Chef', emoji: '🍳' },
  maid: { label: 'Maid / Cleaner', emoji: '🧹' },
  driver: { label: 'Driver', emoji: '🚗' },
  gardener: { label: 'Gardener', emoji: '🪴' },
  pet_care: { label: 'Pet care', emoji: '🐶' },
  childcare: { label: 'Childcare / Nanny', emoji: '👶' },
  tutor: { label: 'Tutor', emoji: '📚' },
  mover: { label: 'Mover / Packer', emoji: '📦' },
  doctor: { label: 'Doctor (on-call)', emoji: '🏥' },
  salon: { label: 'Salon (home)', emoji: '💇' },
  handyman: { label: 'Handyman', emoji: '🛠️' },
  other: { label: 'Other', emoji: '🌿' },
};

function StarRating({ value, onChange, size = 'sm', readonly = false }) {
  // When readonly, render <span>s — important when this is rendered inside another <button> (e.g., card)
  if (readonly) {
    return (
      <span className="inline-flex items-center gap-0.5" data-testid="star-rating">
        {[1, 2, 3, 4, 5].map((s) => (
          <span
            key={s}
            className={`${s <= value ? 'text-amber-400' : 'text-slate-300'} ${size === 'lg' ? 'inline-block w-7 h-7' : 'inline-block w-4 h-4'}`}
            data-testid={`star-${s}`}
            aria-label={`${s} star${s > 1 ? 's' : ''}`}
          >
            <Star className="w-full h-full fill-current" />
          </span>
        ))}
      </span>
    );
  }
  return (
    <div className="flex items-center gap-0.5" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onChange && onChange(s)}
          className={`cursor-pointer ${s <= value ? 'text-amber-400' : 'text-slate-300'} ${size === 'lg' ? 'w-7 h-7' : 'w-4 h-4'}`}
          data-testid={`star-${s}-pick`}
        >
          <Star className="w-full h-full fill-current" />
        </button>
      ))}
    </div>
  );
}

export function RecommendationsTile({ count, recent, onClick }) {
  return (
    <Card data-testid="recommendations-tile" className="shadow border-0 cursor-pointer hover:shadow-lg" onClick={onClick}>
      <CardContent className="p-3 sm:p-5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl bg-cyan-100 flex items-center justify-center shrink-0">
              <Award className="w-4 h-4 sm:w-6 sm:h-6 text-cyan-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm sm:text-base font-medium text-slate-600 leading-tight">Recommendations</p>
              <p className="text-3xl sm:text-4xl font-extrabold text-cyan-600">{count}</p>
              {recent && <p data-testid="rec-tile-recent" className="text-xs sm:text-xs text-cyan-700/80 truncate mt-0.5">{recent}</p>}
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-300 hidden sm:block shrink-0" />
        </div>
      </CardContent>
    </Card>
  );
}

export function RecommendationsModal({ open, onOpenChange, currentUserId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('categories'); // 'categories' | 'category' | 'detail' | 'add'
  const [activeCategory, setActiveCategory] = useState(null);
  const [active, setActive] = useState(null);
  const [globalSearch, setGlobalSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await recommendationAPI.list(); setItems(data || []); }
    catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const remove = async (rec) => {
    if (!window.confirm(`Delete "${rec.business_name}"?`)) return;
    try { await recommendationAPI.remove(rec.id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // Group items by category, count + ratings summary
  const categories = Object.entries(CATEGORY_META).map(([key, meta]) => {
    const inCat = items.filter(it => it.category === key);
    const totalRatings = inCat.reduce((sum, it) => sum + (it.rating_count || 0), 0);
    const avg = totalRatings > 0
      ? inCat.reduce((sum, it) => sum + (it.rating_avg || 0) * (it.rating_count || 0), 0) / totalRatings
      : 0;
    return { key, ...meta, count: inCat.length, totalRatings, avg };
  }).filter(c => c.count > 0); // only show non-empty categories

  // Empty categories shown at the bottom for discoverability when nothing exists at all
  const emptyCategories = items.length === 0
    ? Object.entries(CATEGORY_META).map(([key, meta]) => ({ key, ...meta, count: 0, totalRatings: 0, avg: 0 }))
    : [];

  const gQ = globalSearch.trim().toLowerCase();
  const globalMatches = gQ
    ? items.filter(it => (it.business_name || '').toLowerCase().includes(gQ)
        || (it.contact_name || '').toLowerCase().includes(gQ)
        || (CATEGORY_META[it.category]?.label || '').toLowerCase().includes(gQ))
    : null;

  const cQ = categorySearch.trim().toLowerCase();
  const categoryItems = activeCategory ? items.filter(it => it.category === activeCategory) : [];
  const categoryFiltered = cQ
    ? categoryItems.filter(it => (it.business_name || '').toLowerCase().includes(cQ) || (it.contact_name || '').toLowerCase().includes(cQ))
    : categoryItems;

  // Top 3 highest-rated contacts community-wide (≥1 rating)
  const topThis = [...items]
    .filter(it => (it.rating_count || 0) > 0)
    .sort((a, b) => (b.rating_avg - a.rating_avg) || (b.rating_count - a.rating_count))
    .slice(0, 3);

  const reset = () => { setView('categories'); setActive(null); setActiveCategory(null); setCategorySearch(''); setGlobalSearch(''); };
  const goBackFromDetail = () => { setActive(null); setView(activeCategory ? 'category' : 'categories'); };

  const renderRecommendationRow = (it) => {
    const meta = CATEGORY_META[it.category] || CATEGORY_META.other;
    return (
      <button
        key={it.id}
        data-testid={`rec-card-${it.id}`}
        onClick={() => { setActive(it); setView('detail'); }}
        className="w-full text-left p-3 bg-white hover:bg-cyan-50 rounded-lg border border-slate-200 transition-colors"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center text-lg flex-shrink-0">{meta.emoji}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="font-semibold text-sm truncate">{it.business_name}</p>
              <Badge variant="outline" className="text-[10px]">{meta.label}</Badge>
            </div>
            {it.contact_name && <p className="text-xs text-slate-500 truncate">{it.contact_name}</p>}
            <div className="flex items-center gap-2 mt-1">
              {it.rating_count > 0 ? (
                <><StarRating value={Math.round(it.rating_avg)} readonly /><span className="text-xs text-slate-500">{it.rating_avg} ({it.rating_count})</span></>
              ) : (
                <span className="text-xs text-slate-400">No ratings yet</span>
              )}
            </div>
            <p className="text-[10px] text-slate-400 mt-0.5">by {it.posted_by_name} · Villa {it.posted_by_villa}</p>
          </div>
          {it.posted_by === currentUserId && (
            <button data-testid={`rec-delete-${it.id}`} onClick={(e) => { e.stopPropagation(); remove(it); }} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
          )}
        </div>
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent data-testid="recommendations-modal" className="max-w-md max-h-[88vh] flex flex-col p-0">
        <DialogHeader className="p-4 pb-3 border-b shrink-0">
          {view === 'detail' && active ? (
            <DialogTitle className="flex items-center gap-2 pr-8 m-0"><button data-testid="rec-back-to-list-btn" onClick={goBackFromDetail}><ArrowLeft className="w-4 h-4" /></button><span className="truncate">{active.business_name}</span></DialogTitle>
          ) : view === 'add' ? (
            <DialogTitle className="flex items-center gap-2 pr-8 m-0"><button onClick={() => setView(activeCategory ? 'category' : 'categories')}><ArrowLeft className="w-4 h-4" /></button>Add a contact</DialogTitle>
          ) : view === 'category' && activeCategory ? (
            <DialogTitle className="flex items-center gap-2 pr-8 m-0"><button data-testid="rec-back-to-categories-btn" onClick={() => { setActiveCategory(null); setCategorySearch(''); setView('categories'); }}><ArrowLeft className="w-4 h-4" /></button><span className="text-lg">{CATEGORY_META[activeCategory]?.emoji}</span><span className="truncate">{CATEGORY_META[activeCategory]?.label}</span><span className="text-xs font-normal text-slate-500 ml-1">({categoryItems.length})</span></DialogTitle>
          ) : (
            <DialogTitle className="m-0">Recommendations</DialogTitle>
          )}
          <DialogDescription className="sr-only">Trusted service providers shared by your neighbors</DialogDescription>
        </DialogHeader>

        {view === 'categories' && (
          <div className="flex-1 overflow-y-auto px-4 py-3">
            <div className="space-y-3">
              <Input data-testid="rec-search" value={globalSearch} onChange={(e) => setGlobalSearch(e.target.value)} placeholder="Search across all categories..." />

              {!gQ && topThis.length > 0 && (
                <div data-testid="rec-top-carousel" className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-cyan-700 flex items-center gap-1.5"><Star className="w-3 h-3 fill-amber-400 text-amber-400" />Most-recommended</p>
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                    {topThis.map(it => {
                      const meta = CATEGORY_META[it.category] || CATEGORY_META.other;
                      return (
                        <button
                          key={it.id}
                          data-testid={`rec-top-${it.id}`}
                          onClick={() => { setActive(it); setView('detail'); }}
                          className="shrink-0 w-44 text-left p-2.5 rounded-lg bg-gradient-to-br from-cyan-500 to-teal-600 text-white hover:from-cyan-600 hover:to-teal-700 transition-colors shadow-sm"
                        >
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className="text-base">{meta.emoji}</span>
                            <span className="text-[10px] font-semibold text-cyan-100 uppercase tracking-wide truncate">{meta.label}</span>
                          </div>
                          <p className="text-sm font-bold truncate">{it.business_name}</p>
                          <div className="flex items-center gap-1 mt-1">
                            <span className="text-amber-300 text-[11px] font-semibold">★ {it.rating_avg.toFixed(1)}</span>
                            <span className="text-[10px] text-cyan-100">· {it.rating_count} {it.rating_count === 1 ? 'rating' : 'ratings'}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mx-auto py-6 text-cyan-500" />
              ) : globalMatches !== null ? (
                globalMatches.length === 0 ? (
                  <p className="text-center text-sm text-slate-500 py-6">No matches for "{globalSearch}".</p>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-500">{globalMatches.length} match{globalMatches.length === 1 ? '' : 'es'}</p>
                    {globalMatches.map(renderRecommendationRow)}
                  </div>
                )
              ) : items.length === 0 ? (
                <div className="space-y-2 py-2">
                  <p className="text-center text-sm text-slate-500 mb-3">Be the first to recommend a service provider!</p>
                  {emptyCategories.map(c => (
                    <button key={c.key} data-testid={`rec-cat-row-${c.key}`} onClick={() => { setActiveCategory(c.key); setView('add'); }} className="w-full flex items-center gap-3 p-3 bg-white hover:bg-cyan-50 rounded-lg border border-slate-200">
                      <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center text-lg flex-shrink-0">{c.emoji}</div>
                      <div className="flex-1 text-left">
                        <p className="text-sm font-semibold text-slate-700">{c.label}</p>
                        <p className="text-[11px] text-slate-400">No contacts yet — tap to add one</p>
                      </div>
                      <Plus className="w-4 h-4 text-cyan-500" />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {categories.map(c => (
                    <button
                      key={c.key}
                      data-testid={`rec-cat-row-${c.key}`}
                      onClick={() => { setActiveCategory(c.key); setView('category'); setCategorySearch(''); }}
                      className="w-full flex items-center gap-3 p-3 bg-white hover:bg-cyan-50 rounded-lg border border-slate-200 transition-colors"
                    >
                      <div className="w-11 h-11 rounded-lg bg-cyan-100 flex items-center justify-center text-xl flex-shrink-0">{c.emoji}</div>
                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-sm font-semibold text-slate-800 truncate">{c.label}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-slate-500">{c.count} contact{c.count === 1 ? '' : 's'}</span>
                          {c.totalRatings > 0 && (
                            <span className="text-[11px] text-amber-600 font-semibold">★ {c.avg.toFixed(1)}</span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {view === 'category' && activeCategory && (
          <div className="flex-1 overflow-y-auto px-4 py-3">
            <div className="space-y-3">
              <Input data-testid="rec-category-search" value={categorySearch} onChange={(e) => setCategorySearch(e.target.value)} placeholder={`Search in ${CATEGORY_META[activeCategory]?.label}...`} />
              {categoryFiltered.length === 0 ? (
                <p className="text-center text-sm text-slate-500 py-6">{categoryItems.length === 0 ? 'No contacts in this category yet.' : 'No matches.'}</p>
              ) : (
                <div className="space-y-2">{categoryFiltered.map(renderRecommendationRow)}</div>
              )}
            </div>
          </div>
        )}

        {view === 'add' && <AddRecommendationView preselectedCategory={activeCategory} onAdded={() => { setView(activeCategory ? 'category' : 'categories'); load(); }} onCancel={() => setView(activeCategory ? 'category' : 'categories')} />}

        {view === 'detail' && active && (
          <div className="flex-1 overflow-y-auto px-4 py-3">
            <RecommendationDetail rec={active} currentUserId={currentUserId} onUpdate={load} />
          </div>
        )}

        {(view === 'categories' || view === 'category') && (
          <div className="border-t p-3 bg-white shrink-0">
            <Button
              data-testid="add-recommendation-btn"
              onClick={() => setView('add')}
              className="w-full bg-cyan-600 hover:bg-cyan-700 h-11"
            >
              <Plus className="w-4 h-4 mr-2" />
              {activeCategory ? `Add ${CATEGORY_META[activeCategory]?.label}` : 'Add Contact'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AddRecommendationView({ preselectedCategory, onAdded, onCancel }) {
  const [form, setForm] = useState({ category: preselectedCategory || 'plumber', business_name: '', contact_name: '', phone: '', description: '' });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.business_name.trim()) return toast.error('Business name is required');
    if (!form.phone.trim()) return toast.error('Phone is required');
    setSubmitting(true);
    try { await recommendationAPI.create(form); toast.success('Contact added'); onAdded(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
      <div>
        <Label>Category</Label>
        <select data-testid="rec-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full p-2 border rounded-md text-sm">
          {Object.entries(CATEGORY_META).map(([k, m]) => <option key={k} value={k}>{m.emoji} {m.label}</option>)}
        </select>
      </div>
      <div><Label>Business / Person Name</Label><Input data-testid="rec-form-business" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="e.g., Ravi's Plumbing" /></div>
      <div><Label>Contact Person (optional)</Label><Input data-testid="rec-form-contact" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="e.g., Ravi" /></div>
      <div><Label>Phone</Label><Input data-testid="rec-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91..." /></div>
      <div><Label>Why do you recommend them?</Label><Textarea data-testid="rec-form-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Quick, fair pricing, did our kitchen tap last week..." /></div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={onCancel} className="flex-1">Cancel</Button>
        <Button data-testid="rec-form-submit" onClick={submit} disabled={submitting} className="flex-1 bg-cyan-600 hover:bg-cyan-700">
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Add
        </Button>
      </div>
    </div>
  );
}

function RecommendationDetail({ rec, currentUserId, onUpdate }) {
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myStars, setMyStars] = useState(rec.my_rating || 0);
  const [myComment, setMyComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const meta = CATEGORY_META[rec.category] || CATEGORY_META.other;
  const isAuthor = rec.posted_by === currentUserId;

  useEffect(() => {
    recommendationAPI.ratings(rec.id).then(r => {
      setRatings(r.data || []);
      const mine = (r.data || []).find(x => x.user_id === currentUserId);
      if (mine) { setMyStars(mine.stars); setMyComment(mine.comment || ''); }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [rec.id, currentUserId]);

  const submit = async () => {
    if (myStars === 0) return toast.error('Pick a star rating');
    setSubmitting(true);
    try { await recommendationAPI.rate(rec.id, myStars, myComment); toast.success('Thanks for rating!'); onUpdate(); recommendationAPI.ratings(rec.id).then(r => setRatings(r.data || [])); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-3 py-2">
      <div className="p-3 bg-cyan-50 border border-cyan-200 rounded-lg">
        <div className="flex items-start gap-2">
          <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center text-lg">{meta.emoji}</div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold">{rec.business_name}</p>
            {rec.contact_name && <p className="text-xs text-slate-600">{rec.contact_name}</p>}
            <a href={`tel:${rec.phone}`} className="inline-flex items-center gap-1 text-xs text-emerald-700 mt-1"><Phone className="w-3 h-3" /> {rec.phone}</a>
            {rec.description && <p className="text-xs text-slate-700 mt-1 italic">"{rec.description}"</p>}
            <p className="text-[10px] text-slate-400 mt-1">Recommended by {rec.posted_by_name} · Villa {rec.posted_by_villa}</p>
          </div>
        </div>
      </div>

      {!isAuthor && (
        <div className="p-3 bg-white border-2 border-cyan-200 rounded-lg space-y-2">
          <p className="text-sm font-semibold">{rec.my_rating ? 'Update your rating' : 'Rate this provider'}</p>
          <StarRating value={myStars} onChange={setMyStars} size="lg" />
          <Textarea data-testid="rec-rate-comment" rows={2} value={myComment} onChange={(e) => setMyComment(e.target.value)} placeholder="Optional comment..." />
          <Button data-testid="rec-rate-submit" onClick={submit} disabled={submitting} size="sm" className="bg-cyan-600 hover:bg-cyan-700">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Star className="w-4 h-4 mr-1" />}Submit Rating
          </Button>
        </div>
      )}

      <div className="space-y-1">
        <p className="text-xs font-semibold text-slate-600 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> All Ratings ({ratings.length})</p>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : ratings.length === 0 ? <p className="text-xs text-slate-400">No ratings yet.</p> : (
          <div className="space-y-1.5">
            {ratings.map(r => (
              <div key={r.id} data-testid={`rec-rating-${r.id}`} className="p-2 bg-slate-50 rounded">
                <div className="flex items-center justify-between"><div><span className="text-xs font-semibold">{r.user_name}</span><span className="text-[10px] text-slate-400 ml-1">Villa {r.user_villa}</span></div><StarRating value={r.stars} readonly /></div>
                {r.comment && <p className="text-xs text-slate-600 mt-1">{r.comment}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
